import type { CSSProperties } from 'react';
import { cx } from '../cx';
import { DRAWINGS, type DrawingName } from './drawings';

/**
 * ONE OF OUR APPLE-SHAPE DRAWINGS AT A GIVEN FACTOR (design-system/symbols/README.md "Sizing").
 *
 * The factor `f` multiplies the drawing's own layout box, so two symbols in one context keep
 * Apple's proportions to each other: a triangle is wider than a circle at the same factor, and
 * neither is squeezed into a square. `unit` says what one `f` is measured in - `em` makes the
 * symbol follow the font size of the word beside it (`f = P / 20.48`), `px` pins it.
 *
 * Internal to the component library: `StatusSymbol` and `StepMark` are the public faces.
 */
export interface DrawingProps {
  readonly name: DrawingName;
  /** Width = W x factor, height = H x factor, in `unit`. */
  readonly factor: number;
  readonly unit: 'em' | 'px';
  readonly className?: string | undefined;
  /** Named for a screen reader, or decorative when absent. */
  readonly label?: string | undefined;
  /** Which of its part's sizes this is (`data-size`), for the states page and the tests. */
  readonly sizeName?: string | undefined;
}

/** Four decimals: a sub-pixel rounding the eye cannot see and a test can still read. */
function size(value: number, unit: 'em' | 'px'): string {
  return `${String(Math.round(value * 10_000) / 10_000)}${unit}`;
}

export function Drawing({ name, factor, unit, className, label, sizeName }: DrawingProps) {
  const drawing = DRAWINGS[name];
  const style: CSSProperties = {
    width: size(drawing.width * factor, unit),
    height: size(drawing.height * factor, unit),
  };
  const box = {
    viewBox: `0 0 ${String(drawing.width)} ${String(drawing.height)}`,
    className: cx('inline-block shrink-0', className),
    style,
    'data-drawing': name,
    ...(sizeName === undefined ? {} : { 'data-size': sizeName }),
  } as const;
  const paths = drawing.paths.map((path) =>
    path.kind === 'stroke' ? (
      <path
        key={path.d}
        d={path.d}
        fill="none"
        stroke="currentColor"
        strokeWidth={path.strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    ) : (
      <path
        key={path.d}
        d={path.d}
        fill="currentColor"
        {...(path.kind === 'fill-evenodd' ? { fillRule: 'evenodd' as const } : {})}
      />
    ),
  );
  /* Named or hidden, written out both ways so the SVG alternative-text lint can see it. */
  return label === undefined ? (
    <svg {...box} aria-hidden="true">
      {paths}
    </svg>
  ) : (
    <svg {...box} role="img" aria-label={label}>
      {paths}
    </svg>
  );
}
